import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { useWatch, UseFormReturn } from 'react-hook-form';
import { useLatest } from '@srm/ui/hooks/useLatest';
import { IDocumentTable, ITableColumn } from '@models/Forms/IForms';
import { IFieldElem } from '@models/IFormData';
import { dateToIsoDate } from '@srm/ui/lib/dateFormValue';
import { getTableCellStyleFormulaTraceTarget, getTableColumnFormulaTraceTarget } from '@utils/documentFormulaTraceUtils';
import type { FormulaTraceRuleKind } from '@/context/DocumentFormulaTraceContext';
import { deepEqual } from '@utils/helpers';
import { sameKeys } from '@srm/ui/lib/sameKeys';
import { parseExternalDataSourceArrayKey } from '@utils/externalDataSourceUtils';
import { subscribeFormValueEvents } from '@srm/ui/lib/rhfBridge';
import { collectTableColumns, hasRowNum, ITableColumnSource, TableRow } from './tableColumns';
import { TableFormBridge } from './tableFormBridge';
import { formDataRowSetRules, formDataRowSets } from './formDataRowSets';
import { IEditorRuleValues } from './tableEditors';

/** Суррогаты строк без '|NUM' (данные интеграций/setValues до перенумерации):
 *  общий на все таблицы WeakMap по идентичности объекта. Ключевое требование —
 *  ЕДИНОЕ пространство ключей с getRowId таблицы (TableData): выбор строк
 *  возвращается из DataTable этими же ключами, и '' здесь превращал бы
 *  удаление/мультиредактирование выбранного в no-op, а правила кнопок и
 *  видимость деталей — в общие на все безключевые строки. */
const keylessRows = { map: new WeakMap<object, string>(), next: 0 };

export const rowKeyOf = (row: TableRow): string => {
    if (hasRowNum(row)) return String(row['|NUM']);
    let key = keylessRows.map.get(row);
    if (!key) {
        keylessRows.next += 1;
        key = `__norow-${keylessRows.next}`;
        keylessRows.map.set(row, key);
    }
    return key;
};

export interface IRowColumnRules extends IEditorRuleValues {
    readonly?: boolean;
    required?: boolean;
    /** css-классы стилей-формул колонки (column.styles), сработавших для строки. */
    styleClasses?: string;
}

const DATE_TYPES = ['date', 'datetime', 'nosecdatetime'];
export const isDateColumn = (column: ITableColumnSource['column']) =>
    'valueType' in column && DATE_TYPES.includes(String(column.valueType));

const isFormula = (rule: string | undefined) => !!rule && rule !== 'true' && rule !== 'false';

/** Строк в порции между уступками браузеру. Порция считается параллельно, но
 *  не должна успевать съесть кадр — иначе прокрутка по свежим строкам дёргается. */
const ROW_RULES_CHUNK = 24;

/** Уступка браузеру между порциями. Обязательна: формулы асинхронные, но промисы
 *  поток НЕ отпускают — без макрозадачи весь расчёт слипается в одну задачу и
 *  вкладка стоит колом ровно столько, сколько считаются все формулы разом. */
const yieldToBrowser = () =>
    new Promise<void>((resolve) => {
        setTimeout(resolve, 0);
    });

/** Кэш построчных значений формул, считаемых ПО ТРЕБОВАНИЮ. */
export interface IRowRuleCache<V> {
    /** Значение строки; строка, которой в кэше нет, встаёт в очередь расчёта. */
    get(row: TableRow): V | undefined;
    /** То же, но без постановки в очередь — для обзорных проверок по всем строкам.
     *  Значение устаревает вместе со строкой: правку ячейки читает как «не знаю». */
    peek(row: TableRow): V | undefined;
    /** Последнее известное значение строки, пусть и посчитанное до правки ячейки.
     *  Рисующим правилам оно нужно как «пока не пересчитали»: дефолт «кнопка
     *  видима» на один кадр показывал бы кнопки, которые правило прячет. */
    last(row: TableRow): V | undefined;
    /** Прогреть строки, которые сейчас не рисуются (выбранные для мультиправки). */
    request(rows: readonly TableRow[]): void;
    /** Версия значения строки: меняется, когда `get`/`last` по ней могут ответить иначе, — досчёт строки или
     *  сброс кэша. Строке с кэшем вида (DataTable `rowVersion`) — перерисоваться и заново спросить правило. */
    versionOf(row: TableRow): number;
}

/** Кэш, у которого пополнение видно снаружи: обзорные выборки по всем строкам
 *  (peek) мемоизируются по `version`, иначе они застывают на дефолтах первого
 *  кадра — когда формулы строк ещё не посчитаны. Меняет identity на каждой
 *  досчитанной порции, поэтому отдаётся только там, где обзор действительно
 *  нужен. */
export interface IVersionedRowRuleCache<V> extends IRowRuleCache<V> {
    version: number;
}

/**
 * Каркас построчных асинхронных формул: значения считаются для тех строк, о
 * которых спросили (то есть которые таблица показывает), а не для всего набора.
 *
 * Прежняя версия считала правила сразу по ВСЕМ строкам таблицы. На карточке с
 * 1500 позициями это «строки × колонки с правилами» формул в одной задаче — и
 * поскольку промисы не уступают поток, вкладка замирала целиком на всё время
 * расчёта (замер на живом стенде: одна блокирующая задача ~120 с). Показывает
 * же таблица страницу или окно виртуализации — десятки строк.
 *
 * Строки формы приходят из useWatch, а он КЛОНИРУЕТ значение на каждую
 * нотификацию (identity всегда новая) — поэтому пригодность кэша проверяется по
 * содержимому строки, а проверенная строка кладётся в кэш вместо прежней копии,
 * чтобы следующие чтения шли по ссылке.
 *
 * `watchKey` — снимок значений полей ВНЕ строки, от которых зависят формулы
 * (поля документа и т.п.): его смена обнуляет кэш, иначе он вернул бы правила,
 * посчитанные по старому значению поля.
 */
const useRowRuleCache = <V>(
    enabled: boolean,
    computeRow: (row: TableRow) => Promise<V>,
    deps: readonly unknown[],
    watchKey = '',
): { cache: IRowRuleCache<V>; version: number } => {
    const [tick, bump] = useReducer((value: number) => value + 1, 0);
    const computeRef = useLatest(computeRow);
    const cacheRef = useRef(new Map<string, { row: TableRow; value: V; seq: number }>());
    /** Номер последнего записанного значения: версия строки — номер её записи. */
    const seqRef = useRef(0);
    const queueRef = useRef(new Map<string, TableRow>());
    /** Поколение кэша: смена отменяет результаты идущего прохода. */
    const generationRef = useRef(0);
    const flushingRef = useRef(false);
    const scheduledRef = useRef(false);
    const aliveRef = useRef(true);
    // Флаг поднимается ПРИ КАЖДОМ маунте, а не только при первом: в StrictMode
    // (dev) React монтирует, размонтирует и монтирует компонент заново, и
    // эффект без установки true оставлял бы кэш «мёртвым» на всё время жизни
    // таблицы — правила (readonly/required/стили-формулы) не считались бы
    // никогда, а flush крутил бы пустой проход по кругу (очередь не пустеет →
    // schedule → flush → …).
    useEffect(() => {
        aliveRef.current = true;
        return () => {
            aliveRef.current = false;
        };
    }, []);

    // Сброс кэша считаем в рендере, а не в эффекте: между сменой правила и
    // эффектом успел бы пройти кадр со значениями, посчитанными по прежнему
    // полю документа.
    const signature = [enabled, watchKey, ...deps];
    const prevSignatureRef = useRef(signature);
    const prev = prevSignatureRef.current;
    if (prev.length !== signature.length || signature.some((item, index) => item !== prev[index])) {
        prevSignatureRef.current = signature;
        generationRef.current += 1;
        cacheRef.current = new Map();
        queueRef.current = new Map();
    }

    const flush = useCallback(async () => {
        scheduledRef.current = false;
        if (flushingRef.current) return;
        flushingRef.current = true;
        const generation = generationRef.current;
        try {
            while (queueRef.current.size > 0 && generation === generationRef.current && aliveRef.current) {
                const chunk = [...queueRef.current].slice(0, ROW_RULES_CHUNK);
                const values = await Promise.all(chunk.map(([, row]) => computeRef.current(row)));
                if (generation !== generationRef.current || !aliveRef.current) return;
                chunk.forEach(([key, row], index) => {
                    // строку могли перезапросить с новым содержимым, пока считалась порция
                    if (queueRef.current.get(key) === row) queueRef.current.delete(key);
                    seqRef.current += 1;
                    cacheRef.current.set(key, { row, value: values[index], seq: seqRef.current });
                });
                bump();
                if (queueRef.current.size > 0) await yieldToBrowser();
            }
        } finally {
            flushingRef.current = false;
        }
    }, [computeRef]);

    const schedule = useCallback(() => {
        if (scheduledRef.current || flushingRef.current) return;
        scheduledRef.current = true;
        setTimeout(() => {
            void flush().then(() => {
                // строки, добавленные в очередь на выходе из прохода
                if (queueRef.current.size > 0) schedule();
            });
        }, 0);
    }, [flush]);

    /** Пригодное значение из кэша: строка совпадает по ссылке либо по содержимому. */
    const reusable = useCallback((key: string, row: TableRow) => {
        const cached = cacheRef.current.get(key);
        if (!cached) return undefined;
        if (cached.row === row) return cached;
        if (!deepEqual(cached.row, row)) return undefined;
        // клон той же строки — запоминаем его, чтобы следующие чтения шли по ссылке
        cached.row = row;
        return cached;
    }, []);

    const enqueue = useCallback(
        (rows: readonly TableRow[]) => {
            if (!enabled) return;
            let added = false;
            for (const row of rows) {
                const key = rowKeyOf(row);
                if (reusable(key, row) || queueRef.current.get(key) === row) continue;
                queueRef.current.set(key, row);
                added = true;
            }
            if (added) schedule();
        },
        [enabled, reusable, schedule],
    );

    const cache = useMemo(
        () => ({
            get: (row: TableRow) => {
                if (!enabled) return undefined;
                const cached = reusable(rowKeyOf(row), row);
                if (cached) return cached.value;
                enqueue([row]);
                return undefined;
            },
            // как get, но без постановки в очередь: значение засчитывается, только
            // если посчитано для ЭТОГО содержимого строки. Иначе обзор по всем
            // строкам верил бы правилам, посчитанным до правки ячейки, — а строку,
            // которой обзор не дал места в разметке, никто и не пересчитает
            peek: (row: TableRow) => reusable(rowKeyOf(row), row)?.value,
            last: (row: TableRow) => cacheRef.current.get(rowKeyOf(row))?.value,
            request: enqueue,
            // непосчитанная строка — отрицательным поколением: после сброса кэша она обязана перерисоваться и
            // встать в очередь заново (сброс очищает и очередь)
            versionOf: (row: TableRow) => cacheRef.current.get(rowKeyOf(row))?.seq ?? -1 - generationRef.current,
        }),
        [enabled, enqueue, reusable],
    );
    // Сам кэш стабилен — колонки и панели не пересобираются на каждой
    // досчитанной порции; наблюдаемую версию берут только те, кому нужен обзор.
    //
    // В версию входит и поколение: сброс кэша (сменилось поле документа, от
    // которого зависит правило) обязан быть виден снаружи. Иначе, когда правило
    // спрятало вид целиком, круг не размыкается — колонки нет, `get` никто не
    // зовёт, строки не менялись, а обзор не пересчитывается, потому что версия
    // прежняя: кнопки и подтаблицы не вернулись бы до правки строк или ремаунта.
    return { cache, version: tick + generationRef.current };
};

/** Пауза перед пересчётом ПОСТРОЧНЫХ правил по внешнему полю: значение поля
 *  документа приходит на каждое нажатие клавиши, а пересчёт стоит
 *  «строки × колонки» формул — считаем по осевшему значению. */
const ROW_RULES_WATCH_DELAY = 200;

const useDebouncedValue = <T>(value: T, delay: number): T => {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(value), delay);
        return () => clearTimeout(timer);
    }, [value, delay]);
    return debounced;
};

/** Версия тяжёлого (массивного) watch-значения. */
interface IHeavyWatchState {
    /** Счётчик изменений значения: события формы по пути + необъяснённые смены ссылки. */
    version: number;
    /** Версия на последнем вычислении ключа: смена ссылки при version === lastSeenVersion
     *  означает изменение БЕЗ события по пути (reset) и бампает версию сама. */
    lastSeenVersion: number;
    /** Ссылка значения на последнем вычислении ключа. */
    lastIdentity: unknown;
}

/** Событие формы по пути `name` затрагивает watch-путь `path` (сам путь, его
 *  вложенность — правка ячейки строки, или родитель — запись всего поля). */
const touchesWatchPath = (name: string, path: string): boolean =>
    name === path || name.startsWith(`${path}.`) || path.startsWith(`${name}.`);

/** Ключ-реактивность формул: useWatch по watch-полям + сравнение по значению
 *  (useWatch с массивом имён отдаёт НОВЫЙ массив каждый рендер — реактивиться
 *  по identity нельзя, эффект зацикливается).
 *
 *  Скаляры сериализуются как раньше (дёшево), а МАССИВЫ (строки таблиц — на
 *  «Заявке на закупку» это 1497×180 полей, ~11 МБ JSON, сериализация ~20 мс
 *  на каждый рендер каждого хука правил) в ключ входят версией, а не
 *  содержимым. Версия растёт от событий формы по пути значения (все записи
 *  строк идут через RHF: точечный setValue ячейки, setValue всего поля,
 *  загрузка линков) и от смены ссылки значения без события по его пути —
 *  это reset (RHF клонирует значения, поэтому любой reset даёт новую ссылку).
 *  Семантика консервативна: возможен лишний пересчёт правил (reset тем же
 *  содержимым), но не пропуск. Не ловится только мутация содержимого массива
 *  на месте БЕЗ события формы — таких записей в таблицы нет (всё через
 *  setValue), а для скаляров сериализация по-прежнему ловит всё. */
export const useFormulaWatchKey = <TFieldValues extends object>(
    formMethods: UseFormReturn<TFieldValues>,
    watches: string[],
): string => {
    const watched = useWatch({
        control: formMethods.control,
        name: watches as never,
        disabled: watches.length === 0,
    });
    const heavyRef = useRef<Map<string, IHeavyWatchState>>(new Map());

    useEffect(() => {
        if (watches.length === 0) return undefined;
        // Без подписки на значения: им RHF клонировал бы всю форму на каждое событие (rhfBridge)
        return subscribeFormValueEvents(formMethods.control, ({ name: changed }) => {
            if (changed === undefined) return; // reset ловится сменой ссылки значения
            heavyRef.current.forEach((state, path) => {
                if (touchesWatchPath(changed, path)) state.version += 1;
            });
        });
    }, [formMethods, watches]);

    if (watches.length === 0) return '';

    const values = (watched ?? []) as unknown[];
    return watches
        .map((path, index) => {
            const value = values[index];
            if (!Array.isArray(value)) return JSON.stringify(value ?? null);

            let state = heavyRef.current.get(path);
            if (!state) {
                state = { version: 0, lastSeenVersion: 0, lastIdentity: value };
                heavyRef.current.set(path, state);
            }
            if (state.lastIdentity !== value) {
                // смена ссылки, не объяснённая событием по пути, — это reset
                if (state.version === state.lastSeenVersion) state.version += 1;
                state.lastIdentity = value;
            }
            state.lastSeenVersion = state.version;
            return `#v${state.version}`;
        })
        // Разделитель обязателен: без него соседние JSON-значения склеиваются
        // ('12'+'3' и '1'+'23' дали бы один ключ).
        .join('\u0001');
};

/** Доступ к правилам колонок по строке. */
export interface IColumnRulesAccess {
    /** Правила ячейки; строка, которой ещё нет в кэше, встаёт в очередь расчёта. */
    for(row: TableRow, columnId: string): IRowColumnRules | undefined;
    /** То же без постановки в очередь (проверки по строкам, которых не видно). */
    peekFor(row: TableRow, columnId: string): IRowColumnRules | undefined;
    /** Прогреть строки заранее — выбранные для мультиредактирования. */
    request(rows: readonly TableRow[]): void;
    /** Версия правил строки (см. `IRowRuleCache.versionOf`). */
    versionOf(row: TableRow): number;
    /** Версия правил ячейки (DataTable `cellVersion`): у колонки без правил — `undefined`, её ячейку досчёт правил
     *  строки не перерисовывает. */
    cellVersionOf(row: TableRow, columnId: string): number | undefined;
}

/** Асинхронные правила колонок по строкам (readonly-формулы, min/max/workCalendar
 *  дат, стили-формулы): рендер и вход в редактор синхронные, формулы — нет. Пока
 *  правило не посчитано, ячейка считается readonly (безопасный дефолт). */
export const useColumnRules = <TFieldValues extends object>(
    table: IDocumentTable,
    bridge: TableFormBridge,
    formMethods: UseFormReturn<TFieldValues>,
): IColumnRulesAccess => {
    const ruleColumns = useMemo(
        () =>
            collectTableColumns(table).filter(({ column }) => {
                const readonlyRule = 'readonly' in column ? column.readonly : undefined;
                const requiredRule = 'required' in column ? (column as ITableColumn).required : undefined;
                const hasStyles = 'styles' in column && !!column.styles?.style?.length;
                return isFormula(readonlyRule) || isFormula(requiredRule) || isDateColumn(column) || hasStyles;
            }),
        [table],
    );
    const ruleColumnIds = useMemo(() => new Set(ruleColumns.map(({ columnId }) => columnId)), [ruleColumns]);
    // Правила колонок сплошь и рядом смотрят на поля ДОКУМЕНТА («Переторжка»,
    // «Тип заявки»): без подписки на них ячейка оставалась запертой (или, наоборот,
    // необязательной) до ближайшей правки строк — правило считалось только по rows.
    const watches = useMemo(() => {
        const formulas: Array<string | undefined> = [];
        ruleColumns.forEach(({ column }) => {
            const col = column as ITableColumn;
            if (isFormula(col.readonly)) formulas.push(col.readonly);
            if (isFormula(col.required)) formulas.push(col.required);
            if (isDateColumn(column)) {
                formulas.push(col.min, col.max);
                if (col.workCalendarRules !== 'true') formulas.push(col.workCalendarRules);
            }
            if ('styles' in column) column.styles?.style?.forEach((style) => formulas.push(style.condition));
        });
        return bridge.getWatchesByFormula(formulas);
    }, [bridge, ruleColumns]);
    const watchedKey = useDebouncedValue(useFormulaWatchKey(formMethods, watches), ROW_RULES_WATCH_DELAY);
    const { cache: rowCache } = useRowRuleCache<ReadonlyMap<string, IRowColumnRules>>(
        ruleColumns.length > 0,
        async (row) => {
            const entries = await Promise.all(
                ruleColumns.map(async ({ column, columnId }): Promise<[string, IRowColumnRules]> => {
                    const col = column as ITableColumn;
                    const rules: IRowColumnRules = {};
                    // target'ы инспектора формул — только при открытом реестре
                    const trace = (kind: FormulaTraceRuleKind) =>
                        bridge.traceEnabled ? getTableColumnFormulaTraceTarget(table, column, kind, row) : undefined;
                    try {
                        const [readonly, required] = await Promise.all([
                            isFormula(col.readonly)
                                ? bridge.evalTableRuleValue(col.readonly, row, undefined, trace('readonly'))
                                : undefined,
                            isFormula(col.required)
                                ? bridge.evalTableRuleValue(col.required, row, undefined, trace('required'))
                                : undefined,
                        ]);
                        if (isFormula(col.readonly)) rules.readonly = !!readonly;
                        if (isFormula(col.required)) rules.required = !!required;
                        if (isDateColumn(column)) {
                            const [min, max, workCalendar] = await Promise.all([
                                col.min
                                    ? bridge.evalTableRuleValue(col.min, row, undefined, trace('min'))
                                    : undefined,
                                col.max
                                    ? bridge.evalTableRuleValue(col.max, row, undefined, trace('max'))
                                    : undefined,
                                col.workCalendarRules && col.workCalendarRules !== 'true'
                                    ? bridge.evalTableRuleValue(
                                          col.workCalendarRules,
                                          row,
                                          undefined,
                                          trace('workCalendar'),
                                      )
                                    : undefined,
                            ]);
                            if (col.min) rules.minDate = dateToIsoDate(min) ?? undefined;
                            if (col.max) rules.maxDate = dateToIsoDate(max) ?? undefined;
                            if (col.workCalendarRules) {
                                rules.workCalendar = col.workCalendarRules === 'true' || !!workCalendar;
                            }
                        }
                        if ('styles' in column && column.styles?.style?.length) {
                            const hits = await Promise.all(
                                column.styles.style.map((style, index) =>
                                    bridge
                                        .evalTableRuleValue(
                                            style.condition,
                                            row,
                                            undefined,
                                            bridge.traceEnabled
                                                ? getTableCellStyleFormulaTraceTarget(
                                                      table,
                                                      column,
                                                      style.cellStyle,
                                                      index,
                                                      row,
                                                  )
                                                : undefined,
                                        )
                                        .catch(() => false),
                                ),
                            );
                            const classes = column.styles.style
                                .filter((_, index) => hits[index])
                                .map((style) => style.cellStyle);
                            if (classes.length) rules.styleClasses = classes.join(' ');
                        }
                    } catch {
                        if (isFormula(col.readonly)) rules.readonly = true;
                    }
                    return [columnId, rules];
                }),
            );
            return new Map(entries);
        },
        [ruleColumns, bridge],
        watchedKey,
    );
    return useMemo(
        () => ({
            // get ставит строку в очередь, last отдаёт прежнее решение, пока идёт
            // расчёт: без него правка ячейки на кадр снимала бы с колонок правила
            for: (row: TableRow, columnId: string) => (rowCache.get(row) ?? rowCache.last(row))?.get(columnId),
            peekFor: (row: TableRow, columnId: string) => rowCache.peek(row)?.get(columnId),
            request: (rows: readonly TableRow[]) => rowCache.request(rows),
            versionOf: (row: TableRow) => rowCache.versionOf(row),
            cellVersionOf: (row: TableRow, columnId: string) =>
                ruleColumnIds.has(columnId) ? rowCache.versionOf(row) : undefined,
        }),
        [rowCache, ruleColumnIds],
    );
};

/** Видимость колонок по формулам (visibilityRules — общие, не построчные):
 *  реактивность через useWatch на поля формул. */
export const useColumnVisibilityRules = <TFieldValues extends object>(
    table: IDocumentTable,
    bridge: TableFormBridge,
    formMethods: UseFormReturn<TFieldValues>,
) => {
    const [map, setMap] = useState<ReadonlyMap<string, boolean>>(new Map());
    const ruleColumns = useMemo(
        () =>
            collectTableColumns(table).filter(
                ({ column }) =>
                    'visibilityRules' in column &&
                    (column as ITableColumn).visibilityRules &&
                    (column as ITableColumn).visibilityRules !== 'true',
            ),
        [table],
    );
    const watches = useMemo(
        () => bridge.getWatchesByFormula(ruleColumns.map(({ column }) => (column as ITableColumn).visibilityRules)),
        [bridge, ruleColumns],
    );
    const watchedKey = useFormulaWatchKey(formMethods, watches);
    useEffect(() => {
        if (ruleColumns.length === 0) {
            setMap((prev) => (prev.size === 0 ? prev : new Map()));
            return;
        }
        let cancelled = false;
        void (async () => {
            const entries = await Promise.all(
                ruleColumns.map(async ({ column, columnId }): Promise<[string, boolean]> => [
                    columnId,
                    await bridge
                        .evalTableFormulaValue(
                            (column as ITableColumn).visibilityRules,
                            undefined,
                            undefined,
                            bridge.traceEnabled
                                ? getTableColumnFormulaTraceTarget(table, column, 'visibility')
                                : undefined,
                        )
                        .then(Boolean)
                        .catch(() => true),
                ]),
            );
            if (!cancelled) {
                const next = new Map(entries);
                setMap((prev) => {
                    if (prev.size === next.size && [...next].every(([k, v]) => prev.get(k) === v)) return prev;
                    return next;
                });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [bridge, ruleColumns, watchedKey]);
    return map;
};

/** Правила кнопок таблицы (добавить/удалить/копировать) — формулы с useWatch-реактивностью.
 *  Дефолты как в старой ветке: add/remove = true, copy = false (копирование только
 *  при заданном и истинном правиле). Наборы добавления из FormData доступны, когда истинны
 *  их visibilityRules и addRowButtonRules (`formDataSets` — ключи доступных).
 *  `settled` — досчитан текущий пересчёт, `ready` — досчитан хотя бы один. */
const NO_SETS: string[] = [];

export const useTableButtonRules = <TFieldValues extends object>(
    table: IDocumentTable,
    bridge: TableFormBridge,
    formMethods: UseFormReturn<TFieldValues>,
) => {
    const [state, setState] = useState({
        canAdd: true,
        canRemove: true,
        canCopy: false,
        formDataSets: NO_SETS,
        evaluation: undefined as { table: IDocumentTable } | undefined,
    });
    const sets = useMemo(() => formDataRowSets(table), [table]);
    const watches = useMemo(
        () =>
            bridge.getWatchesByFormula([
                table.addRowButtonRules,
                table.removeRowButtonRules,
                table.copyRowButtonRules,
                ...formDataRowSetRules(sets),
            ]),
        [bridge, table, sets],
    );
    const watchedKey = useFormulaWatchKey(formMethods, watches);
    const evaluation = useMemo(() => ({ bridge, table, watchedKey }), [bridge, table, watchedKey]);
    useEffect(() => {
        let cancelled = false;
        void (async () => {
            const evalRule = (rule: string | undefined, fallback: boolean, failureFallback = fallback) =>
                rule
                    ? bridge
                          .evalTableFormulaValue(rule)
                          .then(Boolean)
                          .catch(() => failureFallback)
                    : Promise.resolve(fallback);
            const failureAdd = !table.emptyState?.enabled;
            const [canAdd, canRemove, canCopy, setsAllowed] = await Promise.all([
                evalRule(table.addRowButtonRules, true, failureAdd),
                evalRule(table.removeRowButtonRules, true),
                evalRule(table.copyRowButtonRules, false),
                Promise.all(
                    sets.map(async (set) => {
                        const [visible, enabled] = await Promise.all([
                            evalRule(set.config.visibilityRules, true, failureAdd),
                            evalRule(set.config.addRowButtonRules, true, failureAdd),
                        ]);
                        return visible && enabled;
                    }),
                ),
            ]);
            const formDataSets = sets.filter((_, index) => setsAllowed[index]).map((set) => set.key);
            if (!cancelled) {
                setState((prev) => {
                    // прежний массив при том же составе: потребители не пересобирают меню на пересчёте
                    const next = {
                        canAdd,
                        canRemove,
                        canCopy,
                        formDataSets: sameKeys(prev.formDataSets, formDataSets) ? prev.formDataSets : formDataSets,
                        evaluation,
                    };
                    return prev.evaluation === evaluation &&
                        prev.canAdd === next.canAdd &&
                        prev.canRemove === next.canRemove &&
                        prev.canCopy === next.canCopy &&
                        prev.formDataSets === next.formDataSets
                        ? prev
                        : next;
                });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [bridge, table, sets, evaluation]);
    const settled = state.evaluation === evaluation;
    // Stale-while-revalidate: до первого расчёта по этой таблице действия с условием
    // скрыты (наборы — всегда, своё добавление — при emptyState), а на пересчёте
    // держится последнее вычисленное значение: иначе кнопки и пункты меню мигают
    // на каждой смене отслеживаемого поля. Значения другой конфигурации таблицы
    // (смена документа без ремаунта) не держим: ключи наборов у неё свои.
    const ready = state.evaluation?.table === table;
    const holdAdd = !ready && !!table.emptyState?.enabled;
    return {
        ...state,
        canAdd: state.canAdd && !holdAdd,
        formDataSets: ready ? state.formDataSets : NO_SETS,
        settled,
        ready,
    };
};

/** Построчные правила кнопок (singleRowDeleteButtonRules/singleRowCopyButtonRules
 *  и editInFormFormula): формулы по строке и полям документа, прекэш; без правила
 *  (и при сбое вычисления) кнопка видима. */
export const useRowButtonRules = <TFieldValues extends object>(
    table: IDocumentTable,
    bridge: TableFormBridge,
    formMethods: UseFormReturn<TFieldValues>,
) => {
    const delRule = table.singleRowDeleteButtonRules;
    const copyRule = table.singleRowCopyButtonRules;
    const editRule =
        table.editInFormFormula && table.editInFormFormula !== 'true' && table.editInFormFormula !== 'false'
            ? table.editInFormFormula
            : undefined;
    // все три правила идут полным резолвом и сплошь и рядом смотрят на поля
    // документа («{|Document|Переторжка}!=1») — подписка обязательна
    const watches = useMemo(
        () => bridge.getWatchesByFormula([delRule, copyRule, editRule]),
        [bridge, delRule, copyRule, editRule],
    );
    const watchedKey = useDebouncedValue(useFormulaWatchKey(formMethods, watches), ROW_RULES_WATCH_DELAY);
    const { cache, version } = useRowRuleCache(
        !!(delRule || copyRule || editRule),
        async (row) => {
            const [del, copy, edit] = await Promise.all([
                bridge.evalRowRule(delRule, row).catch(() => true),
                bridge.evalRowRule(copyRule, row).catch(() => true),
                bridge.evalRowRule(editRule, row).catch(() => true),
            ]);
            return { del, copy, edit };
        },
        [delRule, copyRule, editRule, bridge],
        watchedKey,
    );
    // С версией: состав командной колонки — обзор по всем строкам, и он обязан
    // пересчитаться, когда правила досчитались (см. IVersionedRowRuleCache).
    return useMemo<IVersionedRowRuleCache<{ del: boolean; copy: boolean; edit: boolean }>>(
        () => ({ ...cache, version }),
        [cache, version],
    );
};

/** Доступные детали строки: видимая подтаблица с данными либо с доступным
 *  добавлением. Пустую таблицу без операций раскрывать незачем. */
export const useDetailVisibility = <TFieldValues extends object>(
    table: IDocumentTable,
    bridge: TableFormBridge,
    formMethods: UseFormReturn<TFieldValues>,
    fields: Record<string, IFieldElem>,
    allowEditing = true,
) => {
    const subTables = table.tables;
    const watches = useMemo(
        () =>
            bridge.getWatchesByFormula(
                (subTables ?? []).flatMap((subTable) => [
                    subTable.visibilityRules,
                    subTable.addRowButtonRules,
                    ...formDataRowSetRules(formDataRowSets(subTable)),
                ]),
            ),
        [bridge, subTables],
    );
    const watchedKey = useDebouncedValue(useFormulaWatchKey(formMethods, watches), ROW_RULES_WATCH_DELAY);
    const { cache, version } = useRowRuleCache(
        !!subTables?.length,
        async (row) => {
            const visible = await Promise.all(
                (subTables ?? []).map(async (subTable) => {
                    const rule = subTable.visibilityRules;
                    if (rule === 'false') return false;
                    // Видимость читается по строке родителя, а правила добавления —
                    // по |Parent|, как в bridge самой подтаблицы.
                    if (
                        rule &&
                        rule !== 'true' &&
                        !(await bridge
                            .evalTableRuleValue(rule, row, row)
                            .then(Boolean)
                            .catch(() => true))
                    )
                        return false;
                    const externalArray = !!parseExternalDataSourceArrayKey(subTable.key);
                    const source = fields[subTable.key];
                    // Внешние строки живут в карте полей, а не внутри строки
                    // родителя. До загрузки пустота источника ещё неизвестна.
                    if (
                        externalArray &&
                        (source?.pending !== false || source.loadFailed || !Array.isArray(source.value))
                    )
                        return true;
                    const data = externalArray ? source.value : row[subTable.key];
                    if (Array.isArray(data) && data.length > 0) return true;
                    if (
                        !allowEditing ||
                        subTable.updateRowsDisabled ||
                        subTable.displayMode === 'view' ||
                        (externalArray && subTable.emptyState?.enabled)
                    )
                        return false;
                    const evalAdd = (addRule: string | undefined) =>
                        addRule
                            ? bridge
                                  .evalTableFormulaValue(addRule, undefined, row)
                                  .then(Boolean)
                                  .catch(() => !subTable.emptyState?.enabled)
                            : Promise.resolve(true);
                    const [canAdd, pickable] = await Promise.all([
                        evalAdd(subTable.addRowButtonRules),
                        Promise.all(
                            formDataRowSets(subTable).map(
                                async (set) =>
                                    (await evalAdd(set.config.visibilityRules)) &&
                                    evalAdd(set.config.addRowButtonRules),
                            ),
                        ),
                    ]);
                    return canAdd || pickable.some(Boolean);
                }),
            );
            return (subTables ?? []).filter((_, index) => visible[index]).map((subTable) => subTable.key);
        },
        [subTables, bridge, fields, allowEditing],
        watchedKey,
    );
    // Версия обновляет обзор служебных треков после асинхронного расчёта правил.
    return useMemo<IVersionedRowRuleCache<string[]>>(() => ({ ...cache, version }), [cache, version]);
};

/** Колонки с включённым мультиредактированием (editRowMulti): правило-формула
 *  на уровне таблицы; пустая строка = включено безусловно (паритет checkEditRowMulti). */
export const useMultiEditColumns = <TFieldValues extends object>(
    table: IDocumentTable,
    bridge: TableFormBridge,
    formMethods: UseFormReturn<TFieldValues>,
) => {
    const [set, setSet] = useState<ReadonlySet<string>>(new Set());
    const ruleColumns = useMemo(
        () =>
            collectTableColumns(table).filter(
                ({ column }) => column.displayMode !== 'view' && 'editRowMulti' in column && (column as ITableColumn).editRowMulti != null,
            ),
        [table],
    );
    const watches = useMemo(
        () =>
            bridge.getWatchesByFormula(
                ruleColumns
                    .map(({ column }) => (column as ITableColumn).editRowMulti)
                    .filter((rule) => rule && rule !== 'true' && rule !== 'false'),
            ),
        [bridge, ruleColumns],
    );
    const watchedKey = useFormulaWatchKey(formMethods, watches);
    useEffect(() => {
        if (ruleColumns.length === 0) {
            setSet((prev) => (prev.size === 0 ? prev : new Set()));
            return;
        }
        let cancelled = false;
        void (async () => {
            const enabled = await Promise.all(
                ruleColumns.map(({ column }) => {
                    const rule = (column as ITableColumn).editRowMulti;
                    if (rule === '') return Promise.resolve(true);
                    // сбой формулы не включает мультиредактирование
                    return bridge
                        .evalTableFormulaValue(rule)
                        .then(Boolean)
                        .catch(() => false);
                }),
            );
            if (!cancelled) {
                const next = new Set(ruleColumns.filter((_, index) => enabled[index]).map(({ columnId }) => columnId));
                setSet((prev) => (prev.size === next.size && [...next].every((k) => prev.has(k)) ? prev : next));
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [bridge, ruleColumns, watchedKey]);
    return set;
};
